import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { formatBedroomBathroom, hasBedroomAndBathroomValues } from '../../utils/formatters';

const BedManagement = ({ beds = [], onBedsChange, propertyType }) => {
  const [openBedDialog, setOpenBedDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // 新增：编辑索引
  const [newBed, setNewBed] = useState({
    bedNumber: '',
    roomNumber: '',
    price: '',
    availableDate: '',
    bedrooms: '',
    bathrooms: '',
  });
  const [errors, setErrors] = useState({}); // 新增：错误提示

  // 确保beds是数组
  const safeBeds = Array.isArray(beds) ? beds : [];

  const handleDeleteBed = (bedIndex) => {
    const updatedBeds = safeBeds.filter((_, i) => i !== bedIndex);
    onBedsChange(updatedBeds);
  };

  const validate = () => {
    const err = {};
    if (!newBed.bedNumber) err.bedNumber = 'Required';
    if (!newBed.roomNumber) err.roomNumber = 'Required';
    if (!newBed.price) err.price = 'Required';
    if (!newBed.availableDate) err.availableDate = 'Required';
    if (propertyType === 'Apartment') {
      if (newBed.bedrooms === '' || newBed.bedrooms === null || newBed.bedrooms === undefined) {
        err.bedrooms = 'Required';
      }
      if (newBed.bathrooms === '' || newBed.bathrooms === null || newBed.bathrooms === undefined) {
        err.bathrooms = 'Required';
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleOpenAdd = () => {
    setEditIndex(null);
    setNewBed({
      bedNumber: '',
      roomNumber: '',
      price: '',
      availableDate: '',
      bedrooms: '',
      bathrooms: '',
    });
    setErrors({});
    setOpenBedDialog(true);
  };

  const handleOpenEdit = (bed, idx) => {
    setEditIndex(idx);
    setNewBed({ ...bed });
    setErrors({});
    setOpenBedDialog(true);
  };

  const handleSave = () => {
    if (!validate()) return;
    if (editIndex !== null) {
      // 编辑
      const updated = [...safeBeds];
      updated[editIndex] = { ...newBed };
      onBedsChange(updated);
    } else {
      // 新增
      onBedsChange([...safeBeds, { ...newBed }]);
    }
    setOpenBedDialog(false);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Bed Management
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add New Bed
        </Button>
      </Box>

      {/* Beds List */}
      {safeBeds.map((bed, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Bed #{bed.bedNumber} (Room #{bed.roomNumber})
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Price: ${bed.price}/month
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Available From: {bed.availableDate}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary">
                {hasBedroomAndBathroomValues(bed.bedrooms, bed.bathrooms)
                  ? formatBedroomBathroom(bed.bedrooms, bed.bathrooms)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                color="primary"
                onClick={() => handleOpenEdit(bed, index)}
              >
                Edit
              </Button>
              <Button
                color="error"
                onClick={() => handleDeleteBed(index)}
              >
                Delete Bed
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Add/Edit Bed Dialog */}
      <Dialog open={openBedDialog} onClose={() => setOpenBedDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editIndex !== null ? 'Edit Bed' : 'Add New Bed'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bed Number"
                value={newBed.bedNumber}
                onChange={(e) => setNewBed(prev => ({ ...prev, bedNumber: e.target.value }))}
                required
                error={!!errors.bedNumber}
                helperText={errors.bedNumber && <span style={{ color: 'red' }}>{errors.bedNumber}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Room Number"
                value={newBed.roomNumber}
                onChange={(e) => setNewBed(prev => ({ ...prev, roomNumber: e.target.value }))}
                required
                error={!!errors.roomNumber}
                helperText={errors.roomNumber && <span style={{ color: 'red' }}>{errors.roomNumber}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                value={newBed.price}
                onChange={(e) => setNewBed(prev => ({ ...prev, price: e.target.value }))}
                required
                error={!!errors.price}
                helperText={errors.price && <span style={{ color: 'red' }}>{errors.price}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Available Date"
                type="date"
                value={newBed.availableDate}
                onChange={(e) => setNewBed(prev => ({ ...prev, availableDate: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.availableDate}
                helperText={errors.availableDate && <span style={{ color: 'red' }}>{errors.availableDate}</span>}
              />
            </Grid>
            {/* 只在Apartment时渲染bedrooms/bathrooms输入框 */}
            {propertyType === 'Apartment' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Bedrooms"
                    type="number"
                    value={newBed.bedrooms}
                    onChange={(e) => setNewBed(prev => ({ ...prev, bedrooms: e.target.value }))}
                    error={!!errors.bedrooms}
                    helperText={
                      <span>
                        {errors.bedrooms ? (
                          <span style={{ color: 'red', display: 'block' }}>{errors.bedrooms}</span>
                        ) : null}
                        <span style={{ opacity: 0.7, fontSize: '0.75rem' }}>If it's a studio, please enter 0 bedrooms 1 bathroom.</span>
                      </span>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Total Bathrooms"
                    type="number"
                    inputProps={{ step: 0.5 }}
                    value={newBed.bathrooms}
                    onChange={(e) => setNewBed(prev => ({ ...prev, bathrooms: e.target.value }))}
                    error={!!errors.bathrooms}
                    helperText={errors.bathrooms && <span style={{ color: 'red' }}>{errors.bathrooms}</span>}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBedDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
          >
            {editIndex !== null ? 'Save' : 'Add Bed'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BedManagement; 